<template>
  <div class="am-finance-view-screen">
    <div v-if="loading" class="am-loading-screen"></div>
    <div v-if="!loading && selectedComponent === ''">
      <div class="am-finance-header am-main-top-box">
        <div class="am-finance-title am-main-top-box-title">Finance</div>
        <div class="am-top-icon">
          <a @click="goBack"><svg-icon class="am-glyphicon-medium am-glyphicon-white" icon="back" title="Back"></svg-icon></a>
          <a href="#" @click="video()"><svg-icon icon="video" title="Process Premium Finance Payments" class="am-glyphicon-medium am-glyphicon-white"></svg-icon></a></div>
      </div>
      <div class="am-finance-main-table">
        <table class="am-finance-table">
          <thead>
          <tr>
            <th class="text-center">E</th>
            <th>Contract #</th>
            <th>Status</th>
            <th>Company</th>
            <th>Date</th>
            <th class="am-text-align-right">APR</th>
            <th class="am-custom-padding-left">Payments</th>
            <th class="am-text-align-right">Premium</th>
            <th class="am-text-align-right">Policy Fee</th>
            <th class="am-text-align-right">Tax</th>
            <th class="am-text-align-right">MGA Fee</th>
            <th class="am-text-align-right">Total</th>
            <th class="am-text-align-right">Amount Financed</th>
            <th class="am-text-align-right am-custom-padding-right">Balance</th>
            <th>Re-Quote</th>
            <th>Attachments</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td class="text-center am-finance-edit">
              <a v-if="financeRecord.contractStatus !== 'Voided'" title="Edit" @click="edit"><svg-icon class="am-glyphicon-medium am-glyphicon-blue" icon="edit" title="Edit"></svg-icon></a>
              <div v-else></div>
            </td>
            <td>{{financeRecord.contractNumber}}</td>
            <td :class="{'am-finance-void': financeRecord.contractStatus === 'Voided' }">{{financeRecord.contractStatus}}</td>
            <td><a :href="financeRecord.company.website" target="_blank">{{financeRecord.company.name}}</a></td>
            <td>{{financeRecord.dateFinanced | moment("MM/DD/YYYY") }}</td>
            <td class="am-text-align-right">{{getAprPayments.apr}}</td>
            <td class="am-custom-padding-left">{{getAprPayments.numberOfPayments}}</td>
            <td class="am-text-align-right">{{formatCurrency(financeRecord.premium)}}</td>
            <td class="am-text-align-right">{{formatCurrency(financeRecord.policyFee)}}</td>
            <td class="am-text-align-right">{{formatCurrency(financeRecord.tax)}}</td>
            <td class="am-text-align-right">{{formatCurrency(financeRecord.mgaFee)}}</td>
            <td class="am-text-align-right">{{getTotalAmount()}}</td>
            <td class="am-text-align-right">{{formatCurrency(financeRecord.amountFinanced)}}</td>
            <td :class="{'am-negative-amount': financeRecord.balance < 0 }" class="am-text-align-right am-custom-padding-right">{{formatCurrency(financeRecord.balance)}}</td>
            <td><a class="am-pointer" v-if="checkReQuote(financeRecord)" href='' @click="requote">Re-Quote</a></td>
            <td v-if="financeRecord.uploaded_images.length > 0" class="text-center"><span class="glyphicon glyphicon-paperclip" title="Attachments"></span></td>
            <td v-else></td>
          </tr>
          </tbody>
        </table>
      </div>
      <accordion header="Policy" width="1080px" :items="policyItems" :is-open="true">
        <template slot="columns">
          <th class="am-custom-padding-left">V</th>
          <th>E</th>
          <th>Policy</th>
          <th>Company</th>
          <th>Status</th>
          <th>Class</th>
          <th class="am-text-align-right am-custom-padding-right">Premium</th>
          <th>Effective</th>
          <th>Expiration</th>
        </template>
        <template slot-scope=row>
          <td class="am-custom-padding-left"><a title="View" @click="changeTab(row.item, 'view')"><svg-icon class="am-glyphicon-medium am-glyphicon-blue" icon="view" title="View"></svg-icon></a></td>
          <td><a title="Edit" @click="changeTab(row.item, 'edit')"><svg-icon class="am-glyphicon-medium am-glyphicon-blue" icon="edit" title="Edit"></svg-icon></a></td>
          <td>{{row.item.policy}}</td>
          <td>{{row.item.product.company.name}}</td>
          <td>{{row.item.status}}</td>
          <td>{{row.item.product.policy_type.name}}</td>
          <td class="am-text-align-right am-custom-padding-right">{{formatCurrency(row.item.premium)}}</td>
          <td>{{row.item.effdate | moment("MM/DD/YYYY")}}</td>
          <td>{{row.item.expdate | moment("MM/DD/YYYY")}}</td>
        </template>
      </accordion>
      <accordion header="Details" width="1080px" :items="detailItems">
        <template slot="columns">
          <th class="am-custom-padding-left">Description</th>
          <th>Date</th>
          <th class="am-text-align-right">Premium</th>
          <th class="am-text-align-right">Policy Fee</th>
          <th class="am-text-align-right">Tax</th>
          <th class="am-text-align-right">MGA Fee</th>
          <th class="am-text-align-right">Total</th>
          <th class="am-text-align-right">Financed</th>
          <th class="am-text-align-right">Amount</th>
          <th class="am-text-align-right am-custom-padding-right">Balance</th>
        </template>
        <template slot-scope=row>
          <td v-if="row.item.type === 'Transfer' && parseFloat(row.item.amount) < 0" class="am-custom-padding-left">{{row.item.type}} From {{row.item.transferContract}}</td>
          <td v-else-if="row.item.type === 'Transfer' && parseFloat(row.item.amount) > 0" class="am-custom-padding-left">{{row.item.type}} To {{row.item.transferContract}}</td>
          <td v-else :class="{'am-finance-void': row.item.type === 'Void Payment' || row.item.type === 'Void Contract' || row.item.subType === 'Voided Payment'}" class="am-custom-padding-left">{{row.item.type}}</td>
          <td>{{row.item.date | moment("MM/DD/YYYY")}}</td>
          <td class="am-text-align-right">{{formatCurrency(row.item.premium, true)}}</td>
          <td class="am-text-align-right">{{formatCurrency(row.item.policyFee, true)}}</td>
          <td class="am-text-align-right">{{formatCurrency(row.item.tax, true)}}</td>
          <td class="am-text-align-right">{{formatCurrency(row.item.mgaFee, true)}}</td>
          <td class="am-text-align-right">{{formatCurrency(row.item.total, true)}}</td>
          <td class="am-text-align-right">{{getAmountFinanced(row)}}</td>
          <td :class="{'am-negative-amount': row.item.amount > 0 }" class="am-text-align-right">{{getTransactionAmount(row)}}</td>
          <td :class="{'am-negative-amount': row.item.balance < 0 }" class="am-text-align-right am-custom-padding-right">{{formatCurrency(row.item.balance)}}</td>
        </template>
      </accordion>
      <accordion header="Payment" width="1080px" :items="paymentItems">
        <template slot="columns">
          <th class="am-custom-padding-left">P</th>
          <th>Receipt</th>
          <th>Office</th>
          <th>Status</th>
          <th class="am-text-align-right">Amount Billed</th>
          <th class="am-text-align-right am-custom-padding-right">Amount Tendered</th>
          <th>Date</th>
        </template>
        <template slot-scope=row>
          <td class="am-custom-padding-left"><a title="Print" @click="printReceipt(row.item)"><svg-icon img="print" class="am-pointer"></svg-icon></a></td>
          <td>{{row.item.receipt}}</td>
          <td>{{row.item.name}}</td>
          <td :class="[ row.item.status !== 1 ? 'am-finance-void' : '' ]">{{getStatus(row.item.status)}}</td>
          <td class="am-text-align-right">{{formatCurrency(row.item.amountBilled)}}</td>
          <td class="am-text-align-right am-custom-padding-right">{{formatCurrency(row.item.amountTendered)}}</td>
          <td>{{row.item.showdate | moment("MM/DD/YYYY")}}</td>
        </template>
      </accordion>
      <accordion header="Attachments" width="1080px" :items="attachmentItems">
        <template slot="columns">
          <th class="am-custom-padding-left">V</th>
          <th>F</th>
          <th>Image Description</th>
          <th>Ext</th>
          <th>Date</th>
        </template>
        <template slot-scope="row" v-if="row.item">
          <td class="am-custom-padding-left"><a title="View" @click="infoAttachment(row.item)" class="am-pointer"><svg-icon class="am-glyphicon-medium am-glyphicon-blue" icon="view" title="view"></svg-icon></a></td>
          <td><a title="Forward"  @click="forwardAttachment(row.item.id)" class="am-pointer"><svg-icon class="am-glyphicon-medium am-glyphicon-blue" icon="forward" title="Forward"></svg-icon></a></td>
          <td>{{row.item.description}}</td>
          <td>{{row.item.fileExtension}}</td>
          <td>{{row.item.dateTime | moment("MM/DD/YYYY")}}</td>
        </template>
      </accordion>
      <accordion header="Log" width="1080px" :items="logItems" :log-view-items="logViewIds" :key="logViewIds">
        <template slot="columns">
          <th class="am-custom-padding-left">View</th>
          <th>Action Performed</th>
          <th>Date</th>
          <th>User</th>
        </template>
        <template slot-scope="row" v-if="row.item">
          <td class="am-custom-padding-left"><a title="View" @click="infoLog(row.item)" class="am-pointer"><svg-icon class="am-glyphicon-medium am-glyphicon-blue" icon="view" title="View"></svg-icon></a></td>
          <td>{{row.item.action}}</td>
          <td>{{row.item.dateTime | moment("MM/DD/YYYY")}}</td>
          <td>{{row.item.user}}</td>
      </template>
      <template slot="expandedView" slot-scope="logViewItemsRow">
        <td colspan="4" class="am-custom-padding-left">
          <view-logs :example="logItems" :items="logItems" :key="logItems" :single-view-row="logViewItemsRow"></view-logs>
        </td>
      </template>
    </accordion>
    <div class="am-finance-view-buttons">
      <button type="button" class="btn btn-success" title="Process Payment" @click="openPaymentDetailsDialog" :disabled="isFinancePaymentDisabled">Payment</button>
      <button type="button" class="btn btn-success" title="Fund" @click="openFundDialog" :disabled="isFinancePaymentDisabled || isFundDisabled">Fund</button>
      <button type="button" class="btn btn-success" title="Additional Premium" @click="openApDialog" :disabled="isFinancePaymentDisabled">A/P</button>
      <button type="button" class="btn btn-success" title="Process Down Payment" @click="processDownPayment" :disabled="isDownPaymentDisabled">Process Down Payment</button>
    </div>
    <div class="am-finance-ribbon-bottom am-main-bottom-box"></div>
    <modal name="fundDialog" width="460px" height="auto" :clickToClose="false" :pivotY="0.4" :adaptive="true" classes="am-vue-modal">
      <fund :financeId="financeId" :unfunded-items="unfundedItems" @refreshDetails="refreshDetails"></fund>
    </modal>
    <modal name="paymentDetails" width="500px" height="auto" :clickToClose="false" :pivotY="0.4" :adaptive="true" classes="am-vue-modal">
      <payment-details :financeId="financeId" :payment-details="paymentDetails" :policy-items="policyItems"></payment-details>
    </modal>
    <modal name="apDialog" width="390px" height="auto" :clickToClose="false" :pivotY="0.4"  :adaptive="true" classes="am-vue-modal">
      <additional-premium :financeId="financeId" :financeRecord="financeRecord" :fundingStatus="paymentDetails.fundingStatus"></additional-premium>
    </modal>
    <modal name="MessagePopup" :clickToClose="false" :pivotY="0.3"  :adaptive="true" width="500px" height="auto" classes="am-vue-modal">
      <message-popup :confirmDelete="false" :errors="errorMessageString" messageHeader="Error" :isError="true"></message-popup>
    </modal>
    <play-video v-show="showVideoModal" :videoCode="videoCode" :videoType="videoType"></play-video>
    </div>
    <div>
      <component :is="selectedComponent" :edit-row="editRow"></component>
    </div>
  </div>
</template>
<script>
import Accordion from './Accordion'
import paymentDetails from './PaymentDetails'
import api from './../http'
import formatCurrency from './../mixins/helper'
import fund from './Fund'
import AdditionalPremium from "./AdditionalPremium"
import email from "./Email"
import viewLogs from "./Finance/ViewLogs"
import MessagePopup from "./MessagePopup"
import PlayVideo from './PlayVideo.vue'

export default {
  data: function () {
    return {
      name: 'FinanceViewScreen',
      selectedComponent: '',
      videoCode: '399958234',
      videoType: 'vimeo',
      showVideoModal: false,
      editRow: {},
      paymentItems: [],
      quoteContract: {},
      isDownPaymentDisabled: false,
      isFinancePaymentDisabled: true,
      isFundDisabled: false,
      unfundedItems: {},
      componentProps: {
        id: this.financeId,
        header: 'Finance',
        tableName: 'premiumFinance'
      },
      logViewIds: [],
      policyItems: [
        {
          policy: 0,
          product: {
            id: 0,
            policy_type: {
              name: '',
              lob: 0,
              line_of_business: {
                id: 0,
                type: ''
              }
            },
            company: {
              name: ''
            }
          },
          status: '',
          premium: 0,
          effdate: '',
          expdate: '',
          cycle_business_purpose: {
            id: 0,
            code: ''
          }
        }
      ],
      detailItems: [
        {
          type: '',
          date: '',
          premium: 0,
          policyFee: '',
          tax: '',
          mga_fee: '',
          total: 0,
          amount: 0,
          balance: 0
        }
      ],
      attachmentItems: [],
      logItems: [],
      paymentDetails: [],
      isError: false,
      loading: false,
      errorMessageString: 'This contract does not exist in the Finance Company Database.\nPlease contact your Premium Finance Company for contract status.' +
          '\n\n\nNote: It may take up to 24 hours for a contract to be approved.'
    }
  },
  mixins: [formatCurrency],
  props: {
    financeId: Number,
    customerId: Number,
    financeRecord: {
      type: Object,
      default: null
    }
  },
  computed: {
    getAprPayments () {
      let data = {
        'apr': '',
        'numberOfPayments': ''
      }
      switch(this.financeRecord.company.premiumfinancesystem) {
        case 'Finance Master':
          if(this.financeRecord.contractStatus === 'Approved') {
            data.apr = this.financeRecord.APR + '%'
            data.numberOfPayments = this.financeRecord.numberPayments
          }
          break
        default:
          data.apr = this.financeRecord.APR + '%'
          data.numberOfPayments = this.financeRecord.numberPayments
      }

      return data
    }
  },
  asyncComputed: {
      paymentItems: {
          get() {
              return api.makeRequest('/finance/payments/' + this.customerId + '/' + this.financeId).then(
                  (data) => {
                      return data.data.results
                  }
              )
          }
      },

      attachmentItems: {
          get() {
              return api.makeRequest("api/v1/attachment/uploaded-images/" + this.financeId + "/premiumFinance/list").then(
                  (data) => {
                      return data.data.results
                  }
              )
          }
      },

      logItems: {
          get() {
              return api.makeRequest("logging/setup-log/" + this.financeId + "/premiumFinance/list").then(
                  (data) => {
                      return data.data.results
                  }
              )
          }
      }
  },
  components: {
    Accordion,
    AdditionalPremium,
    fund,
    paymentDetails,
    email,
    viewLogs,
    MessagePopup,
    PlayVideo
  },
  mounted () {
      this.loading = true
      api.makeRequest('/customer/finance/' + this.financeId + '/policies').then(
      (data) => {
        this.policyItems = data.data.results
        if(this.financeRecord.company.premiumfinancesystem === 'Finance Master' && this.financeRecord.company.finance_company_details.isIntegrated === 1) {

          api.makeRequest('/customer/finance/' + this.financeId + '/payment-details').then(
            (data) => {
              this.paymentDetails = data.data.results
              this.isError = data.data.isError
            }
          );
        }
        this.loading = false
      }
    );

    this.getDetails()

    this.getUnfundedItems();

    if((this.financeRecord.isDownPaymentProcessed == 0 && this.financeRecord.contractStatus == 'Quoted') || (this.financeRecord.contractStatus === 'Voided')) {
      this.isDownPaymentDisabled = true
      this.isFinancePaymentDisabled = true
    }
    else if (this.financeRecord.isDownPaymentProcessed == 0 && this.financeRecord.contractStatus == 'Approved') {
      this.isDownPaymentDisabled = false
      this.isFinancePaymentDisabled = true
    }
    else {
      this.isDownPaymentDisabled = true
      this.isFinancePaymentDisabled = false
    }
  },

  methods: {
    goBack () {
      this.$emit('changeComponent', '')
    },
    video: function () {
      this.showVideoModal = true
      this.$modal.show('playVideo')
    },
    openApDialog () {
      this.$modal.show('apDialog')
    },
    openPaymentDetailsDialog () {
        if(this.isError){
            this.$modal.show('MessagePopup')
        }else{
          if(this.financeRecord.company.premiumfinancesystem === 'Finance Master' && this.financeRecord.company.finance_company_details.isIntegrated === 1) {
            this.$modal.show('paymentDetails')
          }
          else {
            window.location.href = '/customersPayment.php?id=' + this.customerId + '&items=' + this.getPolicyIds() + '&financeAmount=0.00&isFinancePayment=true'
          }
        }
    },
    openFundDialog () {
      this.$modal.show('fundDialog')
    },
    getStatus (status) {
        return status === 1 ? 'Active' : 'Void'
    },
    changeTab (item, mode) {
      let url = ''
      if (mode === 'edit') {
        url = '/ajax_customersDisplay.php?id=' + this.customerId + '&type=edit&pol=' + item.id + '&compabbr=' + item.product.company.abbr + '&prodid=' + item.product.id
      } else {
        url = '/ajax_customersDisplay.php?type=policy&id=' + this.customerId
      }
      this.$store.dispatch('events/fireEvent', { name: 'customer-tab-change', data: url })
    },
    edit () {
      this.$emit('changeComponent', 'FinanceEdit', this.financeId)
    },
    getTotalAmount () {
      return this.formatCurrency(this.financeRecord.premium + this.financeRecord.policyFee + this.financeRecord.tax + this.financeRecord.mgaFee)
    },
    infoAttachment(attachment){
      var fileExtension = (attachment.fileExtension.indexOf(".") === -1) ? "." + attachment.fileExtension : attachment.fileExtension
      window.open("attc2/" + attachment.masterId + "/" +  attachment.yearWeekFolder + "/" + attachment.guId + fileExtension, "_blank")
    },
    forwardAttachment(id){
      let attachmentId = "attachmentIds=" + id
      this.$store.dispatch('events/fireEvent', { name: 'open-vue-attachment-forward', data: attachmentId})
    },
    infoLog(item){
      let checkLogArray = $.inArray(item.id, this.logViewIds)
      if(checkLogArray === -1) {
          this.logViewIds.push(item.id)
      } else {
          this.logViewIds.splice(checkLogArray, 1);
      }
    },
    refreshAttachmentList: function () {
      this.$asyncComputed.attachmentItems.update()
    },
    getPolicyIds() {
      let ids = '';
      this.policyItems.forEach(policy => {
        ids = policy.id + ',' + ids
      })
      return ids.slice(0,ids.length-1);
    },
    processDownPayment () {
      let downPayment = this.formatCurrency(this.financeRecord.downPayment).replace('$', '').replace(',', '')
      let fundingStatus = this.financeRecord.company.finance_company_details.fundingStatus
      let entryType = this.policyItems[0].cycle_business_purpose.code
      let policyIds = this.getPolicyIds()
      let fundingQueryString = ''
      let hasAddon = false
      let amountsIfAddOn = ''

      switch(entryType) {
        case 'RWL':
          entryType = 'REN'
          break;
        default:
          entryType = 'NEW'
      }

      if (fundingStatus === 'Full Funded') {
        fundingQueryString = '&paymentAppendage=DPF'
      }
      else if (fundingStatus === 'Partial Funded') {
        fundingQueryString = '&saveDownPayment=true&paymentAppendage=DPP'
        this.policyItems.forEach(policy => {
          if (policy.product.policy_type.name === 'ADD-ON' && hasAddon === false){
            hasAddon = true
          }
        })
        downPayment = (policyIds.search(",") === -1 || hasAddon) ? downPayment : 0.00
        amountsIfAddOn = hasAddon ? '&amountsIfAddOn=0' + ',' + downPayment : '';
      }

      window.location.href = '/customersPayment.php?id=' + this.customerId + '&items=' + this.getPolicyIds() + '&financeAmount=' + downPayment + '&isProcessDownPayment=true&isPremiumFinance=true&entryType=' + entryType + fundingQueryString + amountsIfAddOn
    },
    printReceipt (row) {
      window.open('payment_receipt.php?id=' + this.customerId + '&receiptNum=' + row.receipt + '&officeID=' + row.office, '_blank')
    },
    requote () {
      this.quoteContract.policyIds = this.getPolicyIds()
      this.quoteContract.policyMinDate = this.getPolicyMinDate()
      this.quoteContract.insuranceType = this.policyItems[0].product.policy_type.line_of_business.type
      this.$store.dispatch('events/fireEvent', { name: 'open-finance-contract', data: "insuranceType="+ this.quoteContract.insuranceType +"&policyIds="+ this.quoteContract.policyIds + "&transactionType=policy&requote=1&policyMinDate=" + this.quoteContract.policyMinDate })
    },
    getPolicyMinDate() {
      let policyEffDates = []
      this.policyItems.forEach(policy => {
        policyEffDates.push(moment(policy.effdate, "YYYY-MM-DD"))
      })

      return moment.min(policyEffDates).format("YYYY-MM-DD")
    },
    getDetails () {
      api.makeRequest('/customer/finance/' + this.financeId + '/details').then(
        (data) => {
          this.detailItems = data.data.results
        }
      )
    },
    getUnfundedItems () {
      return api.makeRequest('/customer/finance/unfunded/' + this.financeId).then(
        (data) => {
          if(data.data.results.length === 0) {
            this.isFundDisabled = true
          }
          this.unfundedItems = data.data.results
        }
      )
    },
    refreshDetails () {
      this.getDetails()
      this.getUnfundedItems()
      this.$emit('refreshList', true)
    },
    isNewRecord (type) {
        let types = [
            'New Business',
            'Additional Premium',
            'Re-Issue',
            'Renewal'
        ]
        return types.includes(type)
    },
    getType (type) {
      let types = [
        'New Business',
        'Rewrite',
        'Re-Issue',
        'Rollover'
      ]

      if(types.includes(type)) {
        type = 'New Business'
      }

      return type
    },
    checkReQuote(financeRecord){
      let isIntegrated = financeRecord.company.finance_company_details !== null ? financeRecord.company.finance_company_details.isIntegrated : 0
      return (financeRecord.contractStatus === 'Quoted' && financeRecord.company.premiumfinancesystem === 'Finance Master' && isIntegrated) ? 1 : 0
    },
    getAmountFinanced (row) {
      let amountFinanced = ''
      switch(row.item.type) {
        case 'Renewal':
        case 'New Business':
        case 'Additional Premium':
          amountFinanced = this.formatCurrency(row.item.amount, true)
          break
        case 'Re-Issue':
          amountFinanced = this.formatCurrency(row.item.total, true)
          break
        default:
          amountFinanced = ''
      }

      return amountFinanced
    },
    getTransactionAmount (row) {
      let transactionAmount = '';
      if(!this.isNewRecord(row.item.type)) {
        transactionAmount = this.formatCurrency(row.item.amount)
      }
      return transactionAmount
    }
  }
}
</script>

<style scoped>
  .am-finance-view-screen {
    width: 1330px;
  }

  .am-finance-header {
    height: 35px;
  }

  .am-finance-title {
    font-size: 14px;
    font-weight: bold;
    float: left;
    margin-left: 25px;
    margin-top: 10px;
  }

  .am-finance-edit {
    width: 25px;
  }

  .am-finance-ribbon-bottom {
    height: 35px;
  }

  .am-finance-main-table {
    padding: 15px;
  }

  .am-finance-table {
    width: 100%;
  }

  .am-finance-table tbody tr:nth-child(odd) {
    background-color: #ECECEC;
  }

  .am-finance-table tbody tr:nth-child(even) {
    background-color: #F4F4F4;
  }

  .am-finance-table thead {
    background-color: #444444;
    color: white;
  }

  .am-finance-table td {
    font-size: 11px;
  }

  .am-finance-table th {
    font-size: 11px;
  }

  .am-finance-view-buttons {
    margin: 10px 0px 5px 15px;
  }

  .am-finance-view-buttons button {
    height: 30px;
    font-size: 12px;
  }

  .am-top-icon {
    float: right;
    padding: 8px;
  }

  .am-pointer {
    cursor: pointer;
  }

  .am-finance-void {
    color: red;
  }
  .v--modal-overlay {
    background-color: rgba(0,0,0,0.6);
  }

  .am-negative-amount {
    color: red;
  }

  .am-text-align-left {
    text-align: left;
  }

  .am-text-align-right {
    text-align: right;
  }

  .am-custom-padding-left {
    padding-left: 10px;
  }

  .am-custom-padding-right {
    padding-right: 15px;
  }

  .am-loading-screen {
    background-color: rgba(255, 255, 255, 0.8);
    position: fixed;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    background-image: url(../assets/spinner-anim2.gif);
    background-position: center center;
    background-repeat: no-repeat;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    background-size: 100px;
  }
</style>

<style>
  .am-vue-modal {
    background-color: white;
    text-align: left;
    border-radius: 0;
    box-shadow: 0 20px 60px -2px rgba(27, 33, 58, 0.4);
    padding: 0;
  }
</style>
